/**
 * capacitorApp
 *
 * Thin, dependency-free access to the Capacitor `App` plugin.
 *
 * Why the bridge global instead of `import('@capacitor/app')`:
 * the JS wrapper package is a convenience layer over `window.Capacitor.Plugins.App`,
 * which the native bridge registers before any app code runs. Reading the global
 * means this workstream adds ZERO new module-graph edges, so the web bundle is
 * unaffected and, just as importantly, the build cannot break if the integrator
 * has not run `npm install` for the Capacitor packages yet. Same reasoning as
 * `src/lib/platform.js` having no imports.
 *
 * Every export is a no-op on the web: `isNative()` is false in every browser.
 */

import { isNative, hasNativePlugin } from '@/lib/platform';

/** The registered App plugin, or null on web / when the plugin is absent. */
export function getAppPlugin() {
  try {
    if (!isNative()) return null;
    if (!hasNativePlugin('App')) return null;
    return window.Capacitor.Plugins.App || null;
  } catch {
    return null;
  }
}

/**
 * Add a listener to the App plugin and get back a plain remove function.
 * Resolves to a no-op remover on web, so callers never need their own guard.
 *
 * @param {string} eventName e.g. 'appStateChange'
 * @param {Function} handler
 * @returns {Promise<() => void>}
 */
export async function addAppListener(eventName, handler) {
  const plugin = getAppPlugin();
  if (!plugin || typeof plugin.addListener !== 'function') return () => {};
  try {
    const handle = await plugin.addListener(eventName, handler);
    return () => {
      try {
        handle?.remove?.();
      } catch {
        /* non-fatal */
      }
    };
  } catch {
    return () => {};
  }
}

/**
 * Open a URL through the OS rather than the webview. Used for the iOS Settings
 * deep link when the microphone has been permanently denied.
 *
 * @param {string} url
 * @returns {Promise<boolean>} true when the OS accepted the request
 */
export async function openNativeUrl(url) {
  const plugin = getAppPlugin();
  if (plugin && typeof plugin.openUrl === 'function') {
    try {
      await plugin.openUrl({ url });
      return true;
    } catch {
      /* fall through to the webview attempt */
    }
  }
  try {
    if (typeof window !== 'undefined') {
      window.location.href = url;
      return true;
    }
  } catch {
    /* non-fatal */
  }
  return false;
}
